import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Clock, Globe, Power, Trash2, X } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import DataSourceSection from './DataSourceSection';
import MappingPicker from './MappingPicker';
import { fmtDate, fmtRelative } from './formatters';
import {
  FREQUENCY_OPTIONS,
  INTERVAL_PRESETS,
  WEEKDAYS,
  browserTimezone,
  defaultScheduleForm,
  formToPayload,
  listTimezones,
  scheduleToForm,
  summarizeSchedule,
} from './scheduleHelpers';

function FieldLabel({ children, hint }) {
  return (
    <div className="ahri-sched__label mb-1">
      <span>{children}</span>
      {hint ? <span className="ahri-sched__hint ms-2">{hint}</span> : null}
    </div>
  );
}

function FrequencyPicker({ value, onChange }) {
  return (
    <div className="ahri-sched__freq row g-2">
      {FREQUENCY_OPTIONS.map((opt) => (
        <div key={opt.value} className="col-6">
          <button
            type="button"
            className={`ahri-sched__freq-btn w-100 ${value === opt.value ? 'is-active' : ''}`}
            onClick={() => onChange(opt.value)}
            aria-pressed={value === opt.value}
          >
            {opt.label}
          </button>
        </div>
      ))}
    </div>
  );
}

function WeekdayPicker({ value, onChange }) {
  const toggle = (day) => {
    const set = new Set(value || []);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    onChange([...set].sort((a, b) => a - b));
  };
  return (
    <div className="ahri-sched__weekdays" role="group" aria-label="Days of week">
      {WEEKDAYS.map((d) => {
        const active = (value || []).includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            className={`ahri-sched__weekday ${active ? 'is-active' : ''}`}
            onClick={() => toggle(d.value)}
            aria-pressed={active}
            aria-label={d.long}
            title={d.long}
          >
            {d.short}
          </button>
        );
      })}
    </div>
  );
}

function ScheduleDrawer({
  open,
  step,
  getAuthHeaders,
  onClose,
  onSaved,
  onDeleted,
  onError,
  onDataSourceSaved,
  onDataSourceDeleted,
}) {
  const [widthPct, setWidthPct] = useState(50);
  const [dragging, setDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartW = useRef(50);

  const [form, setForm] = useState(() => defaultScheduleForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [localError, setLocalError] = useState('');

  const timezones = useMemo(() => listTimezones(), []);
  const browserTz = useMemo(() => browserTimezone(), []);

  useEffect(() => {
    if (!open || !step) return;
    setWidthPct(50);
    setLocalError('');
    setForm(scheduleToForm(step.schedule, browserTz));
  }, [open, step, browserTz]);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const dx = dragStartX.current - e.clientX;
      const dPct = (dx / window.innerWidth) * 100;
      setWidthPct(Math.min(75, Math.max(25, dragStartW.current + dPct)));
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  if (!open || !step) return null;

  const startResize = (e) => {
    e.preventDefault();
    setDragging(true);
    dragStartX.current = e.clientX;
    dragStartW.current = widthPct;
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setOptions = (patch) =>
    setForm((prev) => ({
      ...prev,
      options: { ...(prev.options || {}), ...(patch || {}) },
    }));

  const handleSave = async () => {
    setLocalError('');
    if (form.frequency_type === 'weekly' && (!form.days_of_week || !form.days_of_week.length)) {
      setLocalError('Pick at least one weekday.');
      return;
    }
    if (form.frequency_type === 'interval') {
      const n = Number(form.interval_minutes);
      if (!Number.isFinite(n) || n < 5) {
        setLocalError('Interval must be at least 5 minutes.');
        return;
      }
      const startMin = Number(form.minute);
      if (!Number.isFinite(startMin) || startMin < 0 || startMin > 59) {
        setLocalError('Start minute must be between 0 and 59.');
        return;
      }
    }
    if (step?.mapping_enabled && form.enabled) {
      const opts = form.options || {};
      const hasProductMapping = step.id === 'product_data_sync' && opts.mapping_preset_id;
      const hasInventoryMapping = step.id === 'inventory_sync' && opts.import_profile_name;
      if (!hasProductMapping && !hasInventoryMapping) {
        setLocalError(
          'Pick a saved field mapping before enabling this schedule.',
        );
        return;
      }
    }
    setSaving(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/dashboard/schedules/${step.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(formToPayload(form)),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || `Save failed (${resp.status})`);
      }
      onSaved?.(step, data.schedule);
      onClose?.();
    } catch (err) {
      const message = err.message || 'Unable to save schedule';
      setLocalError(message);
      onError?.(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setLocalError('');
    try {
      const headers = getAuthHeaders();
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/dashboard/schedules/${step.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || `Delete failed (${resp.status})`);
      }
      onDeleted?.(step);
      onClose?.();
    } catch (err) {
      const message = err.message || 'Unable to delete schedule';
      setLocalError(message);
      onError?.(message);
    } finally {
      setDeleting(false);
    }
  };

  const summary = summarizeSchedule({ ...form });
  const tzLabel = form.timezone || 'UTC';
  const isWeekly = form.frequency_type === 'weekly';
  const isDaily = form.frequency_type === 'daily';
  const isHourly = form.frequency_type === 'hourly';
  const isInterval = form.frequency_type === 'interval';

  return (
    <>
      <div
        role="presentation"
        className="ahri-sched__backdrop"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Schedule ${step.label}`}
        className="ahri-sched__drawer"
        style={{ width: `${widthPct}%`, transition: dragging ? 'none' : 'width .15s ease' }}
      >
        <div
          className="ahri-sched__resize"
          onMouseDown={startResize}
          role="separator"
          aria-label="Resize drawer"
          title="Drag to resize"
        />

        <header className="ahri-sched__header">
          <div className="d-flex align-items-start gap-3">
            <span className="ahri-sched__icon">
              <Calendar size={20} strokeWidth={1.75} />
            </span>
            <div>
              <div className="ahri-sched__eyebrow">Schedule pipeline step</div>
              <h2 className="ahri-sched__title h5 mb-1">{step.label}</h2>
              <div className="ahri-sched__subtitle">{step.source || step.description}</div>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light ahri-sched__close"
            onClick={onClose}
            aria-label="Close schedule drawer"
          >
            <X size={16} />
          </button>
        </header>

        <div className="ahri-sched__summary">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <div className="ahri-sched__summary-label">Summary</div>
              <div className="ahri-sched__summary-value">{summary}</div>
              <div className="ahri-sched__summary-tz">
                <Globe size={12} className="me-1 opacity-75" />
                {tzLabel}
              </div>
            </div>
            <div className="text-end">
              <div className="ahri-sched__summary-label">Current next run</div>
              <div className="ahri-sched__summary-value">
                {step.schedule?.next_run_at ? fmtDate(step.schedule.next_run_at) : 'Not scheduled'}
              </div>
              {step.schedule?.next_run_at ? (
                <div className="ahri-sched__summary-tz">{fmtRelative(step.schedule.next_run_at)}</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="ahri-sched__body">
          {localError ? (
            <div className="alert alert-danger py-2 mb-3" role="alert">{localError}</div>
          ) : null}

          <div className="ahri-sched__section">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <FieldLabel>Status</FieldLabel>
              <div className="form-check form-switch m-0">
                <input
                  id="ahri-sched-enabled"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={!!form.enabled}
                  onChange={(e) => setField('enabled', e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="ahri-sched-enabled">
                  <Power size={12} className="me-1 opacity-75" />
                  {form.enabled ? 'Enabled' : 'Paused'}
                </label>
              </div>
            </div>
            <p className="ahri-sched__hint mb-0">
              When paused the schedule stays saved but no runs are queued.
            </p>
          </div>

          <div className="ahri-sched__section">
            <FieldLabel>Frequency</FieldLabel>
            <FrequencyPicker
              value={form.frequency_type}
              onChange={(value) => setField('frequency_type', value)}
            />
          </div>

          {isInterval ? (
            <div className="ahri-sched__section">
              <FieldLabel hint="Minimum 5 minutes">Run every</FieldLabel>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {INTERVAL_PRESETS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`ahri-sched__chip ${Number(form.interval_minutes) === m ? 'is-active' : ''}`}
                    onClick={() => setField('interval_minutes', m)}
                  >
                    {m < 60 ? `${m} min` : `${m / 60} hr`}
                  </button>
                ))}
              </div>
              <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
                <input
                  type="number"
                  min="5"
                  max="10080"
                  step="5"
                  className="form-control"
                  value={form.interval_minutes ?? ''}
                  onChange={(e) => setField('interval_minutes', e.target.value)}
                  aria-label="Custom interval in minutes"
                />
                <span className="input-group-text">minutes</span>
              </div>
              <FieldLabel hint="0–59 · first run each hour at this minute, then every interval">
                Start at minute of hour
              </FieldLabel>
              <div className="input-group input-group-sm mt-2" style={{ maxWidth: 200 }}>
                <span className="input-group-text">:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  className="form-control"
                  value={form.minute ?? 0}
                  onChange={(e) => setField('minute', e.target.value)}
                  aria-label="Start minute of the hour"
                />
              </div>
              <p className="ahri-sched__hint mb-0 mt-2">
                Example: every 15 minutes from :05 runs at :05, :20, :35, and :50.
              </p>
            </div>
          ) : null}

          {isHourly ? (
            <div className="ahri-sched__section">
              <FieldLabel hint="0-59">Minute of the hour</FieldLabel>
              <div className="input-group input-group-sm" style={{ maxWidth: 200 }}>
                <span className="input-group-text">:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  className="form-control"
                  value={form.minute ?? 0}
                  onChange={(e) => setField('minute', e.target.value)}
                  aria-label="Minute of the hour"
                />
              </div>
            </div>
          ) : null}

          {(isInterval || isHourly) ? (
            <div className="ahri-sched__section">
              <FieldLabel hint="Run times are aligned in this timezone">Timezone</FieldLabel>
              <div className="ahri-sched__tz-input input-group input-group-sm">
                <span className="input-group-text" aria-hidden="true">
                  <Globe size={14} />
                </span>
                <select
                  className="form-select"
                  value={form.timezone}
                  onChange={(e) => setField('timezone', e.target.value)}
                  aria-label="Timezone"
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              {form.timezone !== browserTz && browserTz ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0 mt-1"
                  onClick={() => setField('timezone', browserTz)}
                >
                  Use browser timezone ({browserTz})
                </button>
              ) : null}
            </div>
          ) : null}

          {(isDaily || isWeekly) ? (
            <div className="ahri-sched__section">
              <FieldLabel hint="Local time in the selected timezone">Run at</FieldLabel>
              <div className="ahri-sched__time-row">
                <div className="ahri-sched__time-input input-group input-group-sm">
                  <span className="input-group-text" aria-hidden="true">
                    <Clock size={14} />
                  </span>
                  <input
                    type="time"
                    className="form-control"
                    value={`${String(form.hour ?? 0).padStart(2, '0')}:${String(form.minute ?? 0).padStart(2, '0')}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':');
                      setField('hour', Number(h) || 0);
                      setField('minute', Number(m) || 0);
                    }}
                    aria-label="Time of day"
                  />
                </div>
                <div className="ahri-sched__tz-input input-group input-group-sm">
                  <span className="input-group-text" aria-hidden="true">
                    <Globe size={14} />
                  </span>
                  <select
                    className="form-select"
                    value={form.timezone}
                    onChange={(e) => setField('timezone', e.target.value)}
                    aria-label="Timezone"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
              {form.timezone !== browserTz && browserTz ? (
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0 mt-1"
                  onClick={() => setField('timezone', browserTz)}
                >
                  Use browser timezone ({browserTz})
                </button>
              ) : null}
            </div>
          ) : null}

          {isWeekly ? (
            <div className="ahri-sched__section">
              <FieldLabel hint="Pick one or more days">Days of week</FieldLabel>
              <WeekdayPicker
                value={form.days_of_week}
                onChange={(days) => setField('days_of_week', days)}
              />
            </div>
          ) : null}

          {step.schedule?.last_run_at ? (
            <div className="ahri-sched__section ahri-sched__last-run">
              <div className="ahri-sched__label mb-1">Last scheduled run</div>
              <div>{fmtDate(step.schedule.last_run_at)} · {step.schedule.last_run_status || '—'}</div>
            </div>
          ) : null}

          {step.mapping_enabled ? (
            <MappingPicker
              step={step}
              getAuthHeaders={getAuthHeaders}
              value={form.options || {}}
              onChange={(patch) => setOptions(patch)}
              onError={(message) => setLocalError(message)}
            />
          ) : null}

          {step.data_source_enabled ? (
            <DataSourceSection
              step={step}
              getAuthHeaders={getAuthHeaders}
              onSaved={(savedStep, ds) => {
                setLocalError('');
                onDataSourceSaved?.(savedStep, ds);
              }}
              onDeleted={(deletedStep) => {
                onDataSourceDeleted?.(deletedStep);
              }}
              onError={(message) => setLocalError(message)}
            />
          ) : null}
        </div>

        <footer className="ahri-sched__footer">
          <div>
            {step.schedule ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                onClick={handleDelete}
                disabled={deleting || saving}
              >
                <Trash2 size={14} />
                {deleting ? 'Removing…' : 'Remove schedule'}
              </button>
            ) : null}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={onClose}
              disabled={saving || deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={saving || deleting}
            >
              {saving ? 'Saving…' : step.schedule ? 'Update schedule' : 'Save schedule'}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

export default ScheduleDrawer;
